/**
 * 模块: 图表交互逻辑
 * 职责: 处理节点拖拽、hover等交互
 * 限制: 文件<280行
 */

import * as d3 from 'd3';
import { Node } from './types';
import { getNodeRadius, getFontSize, NODE_CONFIG } from './config';

/**
 * 创建拖拽行为
 */
export function createDragBehavior(simulation: d3.Simulation<Node, undefined>) {
  return d3.drag<any, any>()
    .on('start', (event: any, d: Node) => {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    })
    .on('drag', (event: any, d: Node) => {
      d.fx = event.x;
      d.fy = event.y;
    })
    .on('end', (event: any, d: Node) => {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    });
}

/**
 * 添加hover交互效果
 */
export function addHoverInteractions(
  nodeSelection: d3.Selection<SVGCircleElement, Node, SVGGElement, unknown>,
  labelSelection: d3.Selection<SVGTextElement, Node, SVGGElement, unknown>,
  influenceThreshold: number
) {
  const getBaseFontSize = (ld: Node) => {
    const radius = getNodeRadius(ld.influence, ld.label);
    const textLength = ld.label ? ld.label.length : 0;
    return getFontSize(radius, textLength);
  };

  const scaleLabelFontSize = (
    selection: d3.Transition<SVGTextElement, Node, SVGGElement, unknown>,
    scale: number
  ) => {
    selection.attr('font-size', function (this: SVGTextElement, ld: Node) {
      const currentAttribute = this.getAttribute('font-size');
      const currentSize = currentAttribute ? parseFloat(currentAttribute) : getBaseFontSize(ld);
      return currentSize * scale;
    });
  };

  nodeSelection
    .on('mouseenter', (event: MouseEvent, d: Node) => {
      const currentTarget = event.currentTarget as SVGCircleElement;
      const nodeElement = d3.select<SVGCircleElement, Node>(currentTarget);
      const currentRadius = getNodeRadius(d.influence, d.label);

      nodeElement
        .transition()
        .duration(200)
        .attr('r', currentRadius * NODE_CONFIG.hoverScale)
        .attr('stroke-width', NODE_CONFIG.hoverStrokeWidth)
        .attr('opacity', 1);

      labelSelection
        .filter((ld: Node) => ld.id === d.id)
        .transition()
        .duration(200)
        .call((selection) => scaleLabelFontSize(selection, NODE_CONFIG.textHoverScale))
        .attr('font-weight', 'bold');
    })
    .on('mouseleave', (event: MouseEvent, d: Node) => {
      const currentTarget = event.currentTarget as SVGCircleElement;
      const nodeElement = d3.select<SVGCircleElement, Node>(currentTarget);

      nodeElement
        .transition()
        .duration(200)
        .attr('r', getNodeRadius(d.influence, d.label))
        .attr('stroke-width', NODE_CONFIG.strokeWidth)
        .attr('opacity', 0.9);

      labelSelection
        .filter((ld: Node) => ld.id === d.id)
        .transition()
        .duration(200)
        .call((selection) => scaleLabelFontSize(selection, 1))
        .attr('font-weight', (ld: Node) => (ld.influence >= influenceThreshold ? 'bold' : '600'));
    });
}

